import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, Button, Checkbox, Empty, Input, Message, Modal, Select, Space, Table, Tag, Typography } from "@arco-design/web-react";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import moment from "moment";

import Content from "../Layout/Content";
import SummaryCard from "../Layout/SummaryCard";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import { FetchApi, PostApi, DeleteApi } from "../Api/AuthenticatedApi";
import BackToReport from "../Components/BackToReport";
import {
  CREATED_FILTERS, WATCH_SORT_KEYS, filterWatches, loadCreatedFilter, loadHideEmpty, loadNameFilter, loadWatchSort,
  saveCreatedFilter, saveHideEmpty, saveNameFilter, saveWatchSort, shownNote, sortWatches, watchReadiness,
  allShownState, keepShown, moveConfirmText, movedText, moveNoteLines, otherOrgs, toggleAllShown, toggleOne,
} from "./spyWatch";

const { Text, Paragraph } = Typography;

// The list of competitors being watched. A watch is one competitor and holds
// every address planted at them; the counts drive the status, because a watch
// with a single address can't tell a drip from a broadcast no matter how much
// mail it collects.
export default function Spy() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notConfigured, setNotConfigured] = useState(false);
  const [sort, setSort] = useState(loadWatchSort);
  const [hideNoMail, setHideNoMail] = useState(loadHideEmpty);
  const [nameFilter, setNameFilter] = useState(loadNameFilter);
  const [createdFilter, setCreatedFilter] = useState(loadCreatedFilter);
  const shown = useMemo(
    () => filterWatches(sortWatches(rows, sort), { name: nameFilter, created: createdFilter, hideEmpty: hideNoMail }),
    [rows, sort, nameFilter, createdFilter, hideNoMail],
  );
  const note = shownNote(shown.rows.length, rows.length);

  // Chosen rows, for moving to another org. Only rows still shown count.
  const [selected, setSelected] = useState([]);
  const chosen = keepShown(selected, shown.rows);
  const headerBox = allShownState(selected, shown.rows);
  const [orgs, setOrgs] = useState([]);
  const [toOrgId, setToOrgId] = useState(undefined);
  const [moving, setMoving] = useState(false);
  const [moveNotes, setMoveNotes] = useState([]);

  const toggleHideNoMail = (on) => {
    setHideNoMail(on);
    saveHideEmpty(on);
  };

  const changeNameFilter = (text) => {
    setNameFilter(text);
    saveNameFilter(text);
  };

  const changeCreatedFilter = (value) => {
    setCreatedFilter(value);
    saveCreatedFilter(value);
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [signupUrl, setSignupUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    FetchApi(getAccessTokenSilently, "/api/spy/watches/",
      (data) => { setRows(Array.isArray(data) ? data : []); setLoading(false); },
      () => navigate("/login"),
      () => { setLoading(false); Message.error("Failed to load watches"); });
  }, [getAccessTokenSilently, navigate]);

  useEffect(load, [load]);

  // The user's other orgs, for "Move to org".
  useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/me",
      (data) => setOrgs(otherOrgs(data && data.organizations, data && data.active_organization_id)),
      () => {}, () => {});
  }, [getAccessTokenSilently]);

  const move = () => {
    const org = orgs.find((o) => o.id === toOrgId);
    if (!org || !chosen.length) return;
    const ids = chosen.slice();
    Modal.confirm({
      title: "Move to another organization",
      content: moveConfirmText(ids.length, org.name),
      onOk: () => new Promise((resolve) => {
        setMoving(true);
        PostApi(getAccessTokenSilently, "/api/spy/watches/move",
          (result) => {
            setMoving(false);
            Message.success(movedText((result && result.moved) || 0));
            setMoveNotes(moveNoteLines(result && result.notes, rows));
            setSelected([]);
            setToOrgId(undefined);
            load();
            resolve();
          },
          () => { setMoving(false); navigate("/login"); resolve(); },
          (err) => { setMoving(false); Message.error((err && err.message) || "Failed to move"); resolve(); },
          { watch_ids: ids, to_org_id: org.id });
      }),
    });
  };

  const create = () => {
    if (!name.trim()) { Message.warning("Name the competitor"); return; }
    setSaving(true);
    PostApi(getAccessTokenSilently, "/api/spy/watches/",
      (created) => {
        setSaving(false); setModalOpen(false); setName(""); setSignupUrl("");
        if (created && created.watch && created.watch.id) {
          // Straight to the detail screen: the address it just minted is the
          // whole point, and it's there waiting to be copied.
          navigate(`/spy/${created.watch.id}`);
        } else {
          load();
        }
      },
      () => navigate("/login"),
      (err) => {
        setSaving(false);
        const message = (err && err.message) || "Failed to start watching";
        // The server's errNoSpyDomain: no active domain in the spy domain pool.
        if (message.indexOf("no domain to hand out addresses on") > -1) {
          setNotConfigured(true);
          setModalOpen(false);
        } else {
          Message.error(message);
        }
      },
      { name: name.trim(), signup_url: signupUrl.trim() });
  };

  const remove = (e, watch) => {
    e.stopPropagation();
    Modal.confirm({
      title: "Stop watching",
      content: `Stop watching "${watch.name}"? Everything collected from them is deleted too. The addresses stop working, so anything they send later is refused.`,
      okButtonProps: { status: "danger" },
      onOk: () => new Promise((resolve) => {
        DeleteApi(getAccessTokenSilently, `/api/spy/watches/${watch.id}`,
          () => { Message.success("Stopped watching"); load(); resolve(); },
          () => { navigate("/login"); resolve(); },
          () => { Message.error("Failed to stop watching"); resolve(); });
      }),
    });
  };

  // Header click sorts: first click A to Z (low to high), then the reverse.
  const sortable = (key) => ({
    key,
    sorter: true,
    sortDirections: ["ascend", "descend"],
    sortOrder: sort && sort.key === key ? (sort.desc ? "descend" : "ascend") : undefined,
  });

  const onTableChange = (_pagination, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const key = s && s.field;
    if (!WATCH_SORT_KEYS.includes(key)) return;
    // A third click would clear the sort; flip it back to A to Z instead.
    const next = { key, desc: s.direction === "descend" };
    setSort(next);
    saveWatchSort(next);
  };

  // Clicking a box must not open the competitor.
  const stop = (e) => e.stopPropagation();

  const columns = [
    {
      title: (
        <Checkbox
          aria-label="Select all shown"
          checked={headerBox.checked}
          indeterminate={headerBox.indeterminate}
          disabled={!shown.rows.length}
          onChange={(on) => setSelected((cur) => toggleAllShown(cur, shown.rows, on))}
        />
      ),
      dataIndex: "select",
      width: 40,
      render: (_, row) => (
        <span onClick={stop}>
          <Checkbox
            aria-label={`Select ${row.name}`}
            checked={chosen.includes(row.id)}
            onChange={(on) => setSelected((cur) => toggleOne(cur, row.id, on))}
          />
        </span>
      ),
    },
    { title: "Competitor", dataIndex: "name", ...sortable("name") },
    {
      title: "Status",
      dataIndex: "status",
      ...sortable("status"),
      render: (_, row) => {
        const { ready, status } = watchReadiness(row);
        return <Tag color={ready ? "green" : "orange"}>{status}</Tag>;
      },
    },
    { title: "Addresses", dataIndex: "address_count", render: (n) => n || 0, ...sortable("addresses") },
    { title: "Collected", dataIndex: "message_count", render: (n) => n || 0, ...sortable("collected") },
    {
      title: "Unread",
      dataIndex: "unread_count",
      // Bold when there is mail nobody has opened yet.
      render: (n) => (n ? <Text bold>{n}</Text> : 0),
      ...sortable("unread"),
    },
    {
      title: "Created",
      dataIndex: "created_date",
      render: (d) => (d ? <span title={moment(d).format("MMM D, YYYY h:mm A")}>{moment(d).fromNow()}</span> : ""),
      ...sortable("created"),
    },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      render: (_, row) => (
        <Button type="text" icon={<IconDelete />} onClick={(e) => remove(e, row)} aria-label="Stop watching" />
      ),
    },
  ];

  return (
    <Content>
      <BackToReport />
      <SimpleBreadcrumbs elements={[{ name: "Email Spy" }]} />

      <SummaryCard
        title="Email Spy"
        subTitle="Sign up to a competitor's list with an address we give you, and see the sequences they send"
      >
        {notConfigured && (
          <Alert
            type="warning"
            style={{ marginBottom: 12 }}
            title="Not set up on this server yet"
            content="Email Spy has no domain to hand out addresses on right now. A platform admin needs to add one, or finish setting one up, on the Spy domains page (in the user menu)."
          />
        )}

        <Paragraph style={{ color: "var(--color-text-3)" }}>
          Add a competitor and we'll give you an email address. Paste it into their signup
          form. A few days later, add a second address and do it again — two signups on
          different dates is what lets us tell a drip sequence from a one-off blast.
        </Paragraph>

        <Space wrap style={{ marginBottom: 12 }}>
          <Button type="primary" icon={<IconPlus />} onClick={() => setModalOpen(true)}>
            Watch a competitor
          </Button>
          <Button onClick={load}>Refresh</Button>
          <Input
            allowClear
            style={{ width: 200 }}
            value={nameFilter}
            onChange={changeNameFilter}
            placeholder="Filter by name"
            aria-label="Filter by name"
          />
          <Select
            style={{ width: 190 }}
            value={createdFilter}
            onChange={changeCreatedFilter}
            aria-label="Created"
            options={CREATED_FILTERS.map((f) => ({ value: f.value, label: `Created: ${f.label}` }))}
          />
          <Checkbox checked={hideNoMail} onChange={toggleHideNoMail}>Hide ones with no mail yet</Checkbox>
          {note && <Text type="secondary">{note}</Text>}
        </Space>

        {moveNotes.length > 0 && (
          <Alert
            type="info"
            closable
            onClose={() => setMoveNotes([])}
            style={{ marginBottom: 12 }}
            title="About the move"
            content={moveNotes.map((line) => <div key={line}>{line}</div>)}
          />
        )}

        {chosen.length > 0 && (
          <Space wrap style={{ marginBottom: 12 }}>
            <Text bold>{chosen.length} selected</Text>
            {orgs.length ? (
              <>
                <Select
                  style={{ width: 220 }}
                  placeholder="Move to org…"
                  aria-label="Move to org"
                  value={toOrgId}
                  onChange={setToOrgId}
                  options={orgs.map((o) => ({ value: o.id, label: o.name || "Unnamed organization" }))}
                />
                <Button type="primary" disabled={!toOrgId} loading={moving} onClick={move}>Move</Button>
              </>
            ) : (
              <Text type="secondary">You are not in any other organization to move them to.</Text>
            )}
            <Button onClick={() => setSelected([])}>Clear</Button>
          </Space>
        )}

        <Table
          size="small"
          rowKey="id"
          loading={loading}
          columns={columns}
          data={shown.rows}
          onChange={onTableChange}
          pagination={false}
          noDataElement={<Empty description={shown.hidden ? "None match these filters" : "Not watching anyone yet"} />}
          onRow={(row) => ({
            style: { cursor: "pointer" },
            onClick: () => navigate(`/spy/${row.id}`),
          })}
        />
      </SummaryCard>

      <Modal
        visible={modalOpen}
        title="Watch a competitor"
        okText="Create"
        confirmLoading={saving}
        okButtonProps={{ disabled: !name.trim() }}
        onOk={create}
        onCancel={() => setModalOpen(false)}
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">Competitor</Text>
          <Input autoFocus value={name} onChange={setName} onPressEnter={create} placeholder="e.g. Acme Outdoor" />
        </div>
        <div>
          <Text type="secondary">Signup page (optional)</Text>
          <Input value={signupUrl} onChange={setSignupUrl} placeholder="https://acme.com/newsletter" />
          <div style={{ marginTop: 6, fontSize: 12, color: "var(--color-text-3)" }}>
            Saved so we can link you straight back when it's time to plant the second address.
          </div>
        </div>
      </Modal>
    </Content>
  );
}
